import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  CheckCircleIcon,
  ArrowPathIcon,
  RocketLaunchIcon,
  LightBulbIcon,
  ChartBarIcon,
  DocumentTextIcon,
  UserGroupIcon,
  BellIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import Navbar from '../../components/Navbar';
import QuickStartBanner from '../../components/QuickStartBanner';
import Chatbot from '../../components/Chatbot';

export default function QuickStartPage() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const basePath = orgId ? `/${orgId}` : '';

  const steps = [
    {
      number: 1,
      title: 'Connect Your Salesforce Org',
      time: '2 minutes',
      icon: RocketLaunchIcon,
      description: 'Authorize FlowDocs to read your Salesforce flows',
      substeps: [
        'Click "Connect Salesforce Org" button',
        'Choose Production or Sandbox',
        'Authorize FlowDocs (read-only access)',
        'You\'ll be redirected back to FlowDocs'
      ],
      tips: [
        'Need admin permissions? Share this guide with your Salesforce admin',
        'We only request read-only access - we cannot modify your flows',
        'Your credentials are securely stored and encrypted'
      ],
      action: {
        label: 'Connect Salesforce Org',
        path: '/connect-salesforce',
        variant: 'primary'
      }
    },
    {
      number: 2,
      title: 'Check on Your First Sync',
      time: '3-5 minutes',
      icon: ArrowPathIcon,
      description: 'Your first sync starts automatically after connecting',
      substeps: [
        'Go to your Flows page',
        'When all flows are scored your sync is complete'
      ],
      tips: [
        'You can continue using FlowDocs while sync runs in the background',
        'We sync flow metadata, AI summaries, and health scores automatically',
        'Weekly syncs are recommended for most organizations'
      ],
      callout: {
        title: 'What gets synced?',
        items: [
          'Flow metadata (name, type, status)',
          'AI-generated documentation',
          'Health scores and issue detection',
          'Flow interview errors from Salesforce'
        ]
      }
    },
    {
      number: 3,
      title: 'Explore Your Documentation',
      time: 'Your pace',
      icon: ChartBarIcon,
      description: 'Discover insights about your Salesforce flows',
      substeps: [
        <>View your <Link to={`${basePath}/dashboard`} className="text-blue-600 hover:underline">Dashboard</Link> - see overall health scores</>,
        <>Click on any element for quick access to related flows - try clicking on any issue in the Issue Distribution chart</>,
        <>Click on any flow in the <Link to={`${basePath}/flows`} className="text-blue-600 hover:underline">Flows</Link> page to view the flow detail page</>,
        <>Check the Recommended Fixes tab of the Performance Scorecard at the top of the flow detail page for ways to improve performance</>,
        <>Visit the <Link to={`${basePath}/fields`} className="text-blue-600 hover:underline">Fields</Link> page to see which Flows touch which fields. Filter by Object, Field, and keyword</>,
        <>Explore the <Link to={`${basePath}/entry-criteria`} className="text-blue-600 hover:underline">Entry Criteria</Link> page to quickly see what conditions will trigger a flow on any object</>
      ],
      tips: [
        'Flows are graded A, B, C, D, or I (Incomplete)',
        'Focus on Grade D and I flows first for quick wins',
        'Use search to find specific flows by name or description'
      ],
      action: {
        label: 'Go to Dashboard',
        path: `${basePath}/dashboard`,
        variant: 'secondary'
      }
    }
  ];

  const nextSteps = [
    {
      title: 'Invite Your Team',
      description: 'Collaborate with other admins and developers',
      icon: UserGroupIcon,
      path: '/settings#team',
      color: 'blue'
    },
    {
      title: 'Review Low-Grade Flows',
      description: 'Fix issues in flows with Grade D or I',
      icon: DocumentTextIcon,
      path: `${basePath}/flows?grade=D,I`,
      color: 'orange'
    },
    {
      title: 'Export Documentation',
      description: 'Download flow docs for compliance or audits',
      icon: ArrowDownTrayIcon,
      path: `${basePath}/flows`,
      color: 'green'
    }
  ];

  const faqs = [
    {
      question: 'How often should I sync?',
      answer: 'Weekly for most orgs, daily if you\'re actively developing flows.'
    },
    {
      question: 'What permissions does FlowDocs need?',
      answer: 'Read-only access to Flows and flow interview errors. We cannot modify your flows.'
    },
    {
      question: 'Can I try it before subscribing?',
      answer: 'Yes! 7-day free trial, no credit card required.'
    },
    {
      question: 'How does AI documentation work?',
      answer: 'We use Claude AI to analyze your flow metadata and generate plain-English summaries of what each flow does.'
    },
    {
      question: 'What are health grades?',
      answer: 'Flows are graded A-D or I (Incomplete) based on best practices, complexity, error rate, and documentation quality.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <QuickStartBanner />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <RocketLaunchIcon className="h-16 w-16 text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Quick Start Guide
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Get up and running quickly! Follow these three simple steps to start documenting your Salesforce flows.
          </p>
        </div>

        {/* Progress Overview */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 mb-10 border border-blue-200">
          <div className="flex items-center justify-center space-x-4 text-sm">
            <div className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-blue-600 mr-1" />
              <span className="text-gray-700">3 easy steps</span>
            </div>
            <span className="text-gray-400">•</span>
            <div className="flex items-center">
              <ClockIcon className="h-5 w-5 text-blue-600 mr-1" />
              <span className="text-gray-700">~10 minutes total</span>
            </div>
            <span className="text-gray-400">•</span>
            <div className="flex items-center">
              <ShieldCheckIcon className="h-5 w-5 text-blue-600 mr-1" />
              <span className="text-gray-700">Read-only access</span>
            </div>
          </div>
        </div>

        {/* Main Steps */}
        <div className="space-y-8 mb-12">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div key={step.number} className="bg-white rounded-lg shadow-md overflow-hidden">
                {/* Step Header */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <div className="h-12 w-12 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                          <span className="text-2xl font-bold text-white">{step.number}</span>
                        </div>
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-white">{step.title}</h2>
                        <p className="text-blue-100 mt-1">{step.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center text-blue-100">
                        <ClockIcon className="h-5 w-5 mr-1" />
                        <span className="text-sm">{step.time}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step Content */}
                <div className="p-6">
                  {/* Substeps */}
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                      Steps:
                    </h3>
                    <ol className="space-y-2">
                      {step.substeps.map((substep, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium mr-3 mt-0.5">
                            {idx + 1}
                          </span>
                          <span className="text-gray-700">{substep}</span>
                        </li>
                      ))}
                    </ol>
                  </div>

                  {/* Callout Box (if present) */}
                  {step.callout && (
                    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                      <div className="flex items-start">
                        <LightBulbIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-2" />
                        <div>
                          <h4 className="text-sm font-semibold text-blue-900 mb-2">
                            {step.callout.title}
                          </h4>
                          <ul className="space-y-1">
                            {step.callout.items.map((item, idx) => (
                              <li key={idx} className="text-sm text-blue-800 flex items-start">
                                <span className="mr-2">•</span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Tips */}
                  <div className="bg-yellow-50 rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-semibold text-yellow-900 mb-2 flex items-center">
                      <LightBulbIcon className="h-4 w-4 mr-1" />
                      Pro Tips:
                    </h4>
                    <ul className="space-y-1">
                      {step.tips.map((tip, idx) => (
                        <li key={idx} className="text-sm text-yellow-800 flex items-start">
                          <span className="mr-2">•</span>
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Action Button */}
                  {step.action && (
                    <div className="flex justify-end">
                      <Link
                        to={step.action.path}
                        className={`inline-flex items-center px-6 py-3 rounded-md font-medium transition-colors ${
                          step.action.variant === 'primary'
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                        }`}
                      >
                        {step.action.label}
                        <Icon className="ml-2 h-5 w-5" />
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Next Steps Section */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            What's Next?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {nextSteps.map((item) => {
              const Icon = item.icon;
              const colorClasses = {
                blue: 'bg-blue-50 border-blue-200 hover:border-blue-400',
                purple: 'bg-purple-50 border-purple-200 hover:border-purple-400',
                orange: 'bg-orange-50 border-orange-200 hover:border-orange-400',
                green: 'bg-green-50 border-green-200 hover:border-green-400'
              };
              const iconColors = {
                blue: 'text-blue-600',
                purple: 'text-purple-600',
                orange: 'text-orange-600',
                green: 'text-green-600'
              };

              return (
                <Link
                  key={item.title}
                  to={item.path}
                  className={`flex items-start p-4 border-2 rounded-lg transition-all ${colorClasses[item.color as keyof typeof colorClasses]}`}
                >
                  <Icon className={`h-6 w-6 ${iconColors[item.color as keyof typeof iconColors]} mt-0.5 mr-3 flex-shrink-0`} />
                  <div>
                    <h3 className="font-semibold text-gray-900">{item.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* FAQs Section */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index}>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {faq.question}
                </h3>
                <p className="text-gray-700">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Need More Help */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg shadow-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Need More Help?
          </h2>
          <p className="text-blue-100 mb-6">
            Check out our comprehensive guides or contact our support team
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to={`${basePath}/help`}
              className="inline-flex items-center justify-center px-6 py-3 border-2 border-white text-white font-medium rounded-md hover:bg-white hover:text-blue-600 transition-colors"
            >
              Browse Help Center
            </Link>
            <Link
              to={`${basePath}/help/contact-support`}
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-blue-600 font-medium rounded-md hover:bg-blue-50 transition-colors"
            >
              Contact Support
            </Link>
          </div>
        </div>
      </div>

      <Chatbot />
    </div>
  );
}
